/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import { chain, cloneDeep, keyBy } from 'lodash';
import moment from 'moment';

import { adjustPercent } from 'services/Calculation';
import {
  groupingFunctionalMapping,
  mapAccountsToTableData
} from 'services/TableSupport';
import { RECEIVED_ACCOUNTS } from 'state/account/AccountBalancesActions';
import { RECEIVED_ACCOUNT_PERFORMANCE } from 'state/account/AccountPerformanceActions';
import { initialDashboardTableFilteringState } from 'state/account/SelectAccountsFilteringReducer';
import { RECEIVED_MMR_MESSAGES } from 'state/notification/NotificationActions';
import {
  RECEIVED_INTRADAY_PRICING_AUTOMATIC,
  RECEIVED_INTRADAY_PRICING_MANUAL
} from 'state/Actions';

export const accountOverviewTableReducer = (
  state = [],
  action,
  { accounts = [], balances, mmrMessages, view = {} } = {
    accounts: [],
    balances
  }
) => {
  // Will ensure that accounts is an array
  const { type } = action;

  if (
    type !== RECEIVED_ACCOUNTS &&
    type !== RECEIVED_ACCOUNT_PERFORMANCE &&
    type !== RECEIVED_MMR_MESSAGES &&
    type !== RECEIVED_INTRADAY_PRICING_MANUAL &&
    type !== RECEIVED_INTRADAY_PRICING_AUTOMATIC
  ) {
    return state;
  }

  if (type === RECEIVED_MMR_MESSAGES) {
    const { messages } = mmrMessages;
    const updatedGroups = state.map((group) => {
      const { children } = group;
      const updatedChildren = children.map((child) => {
        if (child) {
          const groupWithMMR = cloneDeep(child);
          if (child.details && child.details.name) {
            const {
              details: {
                name: { ticker }
              }
            } = child || {};
            groupWithMMR.mmrMessage =
              messages && ticker ? messages[ticker] : '';
          }
          return groupWithMMR;
        }
      });
      return {
        ...group,
        children: updatedChildren
      };
    });
    return updatedGroups;
  }

  if (type === RECEIVED_ACCOUNT_PERFORMANCE) {
    const performanceById = keyBy(action.accountPerformance, 'accountId');
    const table = [];
    state.forEach((outer) => {
      const children = [];
      outer.children.forEach((row) => {
        if (row && row.details) {
          const accountId =
            row && row.details && row.details.name
              ? row.details.name.accountId
              : '-1';
          if (performanceById[accountId]) {
            const performance = {
              percentReturn: adjustPercent(
                performanceById[accountId].inceptionReturn
              ),
              inceptionDate: performanceById[accountId].inceptionDate
            };
            const details = { ...row.details, performance };
            children.push({ ...row, details });
          } else {
            children.push(row);
          }
        }
      });
      table.push({ ...outer, children });
    });
    return table;
  }

  const currentDate = moment.utc(balances.asOfDate);

  let filteringSelections = {
    ...initialDashboardTableFilteringState
  };

  return chain(accounts)
    .filter({ purgedAccount: false, hiddenAccount: false })
    .orderBy(groupingFunctionalMapping('default', true))
    .groupBy(groupingFunctionalMapping('default'))
    .values()
    .map(
      mapAccountsToTableData({
        filteringSelections,
        currentDate,
        view,
        transformAccount: (v) => v
      })
    )
    .value();
};

accountOverviewTableReducer.priority = 19;
